import { formatDate } from '@angular/common';
import { IDay, IStoreData, Store } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { NgInject } from '@df/ng/ng-inject';
import { Address } from '@df/session/api/address-book/address';
import { TranslationService } from '@df/ui/translation/translation.service';

declare module '@df/catalog/store/store' {
    export interface IStoreDataV1 {
        street_1?: string;
    }
}

export class LocalStore<T extends IStoreData = IStoreData> extends Store<T> {
    @NgInject(TranslationService)
    private translationService!: TranslationService;

    private _shortOpeningHours: string[] | undefined;
    private _addressLine: string | undefined;

    get address(): string {
        const data = this.data;
        if (!this._addressLine && data) {
            this._addressLine = new Address({
                city: data.city,
                postcode: data.postcode,
                street_1: data.address,
                country_id: data.country_id
            }).oneLine;
        }
        return this._addressLine;
    }

    get shortOpeningHours(): string[] | undefined {
        if (!this._shortOpeningHours) {
            const weekDays = this.convertToWeekDaysArray(this.openingHours);

            let rangeStart: number | undefined = undefined;
            let ranges: string[] = [];
            let rangeHours: string | undefined;
            const date = new Date();

            const toDow = (day: number) => {
                date.setDate(date.getDate() + day - date.getDay());
                return formatDate(date, 'E', 'en');
            };

            const finishRange = (rangeEnd: number) => {
                const range = rangeStart === rangeEnd ? toDow(rangeEnd) : `${toDow(rangeStart!)} - ${toDow(rangeEnd)}`;
                ranges.push(`${range}: ${rangeHours}`);
                rangeHours = undefined;
                rangeStart = undefined;
            };
            new Array(7).fill(null).forEach((_, i) => {
                const day = i + 1;
                const hours = weekDays[day] || this.translationService.transform('Closed');
                if (!rangeHours) {
                    rangeStart = day;
                    rangeHours = hours;
                }
                if (rangeHours !== hours) {
                    finishRange(day - 1);
                    rangeStart = day;
                    rangeHours = hours;
                }
                if (day === 7) {
                    finishRange(7);
                }
            });

            if (this.data && this.data.days) {
                this._shortOpeningHours = ranges;
            }
        }
        return this._shortOpeningHours;
    }

    protected override dataSetter(data: T | undefined): void {
        if (data) {
            data.street_1 = data.address;
        }
        super.dataSetter(data);
    }

    private convertToWeekDaysArray(openingHours: IDay[] | undefined): { [key: number]: string } {
        const weekDays = {};
        const shiftedDayIndex = (day: number) => (day === 0 ? 7 : day);
        openingHours?.forEach(day => {
            const dayOfWeek = day.date?.getDay();
            if (dayOfWeek === undefined) {
                return;
            }
            const shiftedDay = shiftedDayIndex(dayOfWeek);
            if (weekDays[shiftedDay] !== undefined) {
                return;
            }
            weekDays[shiftedDay] = day.open === day.close ? this.translationService.transform('Closed') : `${day.open} - ${day.close}`;
        });
        return weekDays;
    }
}
StoreResource.instance = LocalStore;
